import { type MutableRefObject } from 'react';
import { type MenuInputOption } from '@saviynt/design-system';

import type {
  DataProvider,
  FormValues,
  PreviewDataProviderPayload,
} from '../../../../shared/types';
import type { ApiError, ApiResponse } from '../../hooks/useGetDataProvider';

import { getDataProviderPreviewPageSize } from '../../../../shared/utils/dataProviderPageSize';
import { DATA_PROVIDER } from '../../../FormBuilder';
import { injectDefaultOptions } from '../defaultValueUtils';
import { applyValueMatching } from '../valueProcessing';
import { shouldAttachValuesToSearch } from '../valuesToSearch';

import {
  buildDependencies,
  type InitialDataForDependencies,
} from './dependencies';
import { mergePaginatedMultiSelectSelections } from './uiHelpers';
import {
  extractDataProviderDefaultSelection,
  extractDataProviderOptionRows,
  validateApiResponseData,
  validateSelectedOptions,
} from './validation';

const coalesceMultiSelectProcessedValue = (
  processedValue: MenuInputOption | MenuInputOption[] | null,
  defaultSelection: MenuInputOption[]
): MenuInputOption | MenuInputOption[] | null => {
  if (defaultSelection.length === 0) {
    return processedValue;
  }

  if (processedValue == null) {
    return defaultSelection;
  }

  if (Array.isArray(processedValue) && processedValue.length === 0) {
    return defaultSelection;
  }

  return processedValue;
};

const mergeDefaultSelectionIntoOptions = (
  base: MenuInputOption[],
  defaultSelection: MenuInputOption[],
  provider: DataProvider
): MenuInputOption[] => {
  if (defaultSelection.length === 0) {
    return base;
  }

  const entries = defaultSelection.map((o) => ({
    value: String(o.value),
    label: String(o.label ?? o.value),
  }));

  return injectDefaultOptions(base, entries, provider.type);
};

// ============================================================================
// Options Loading Utilities
// ============================================================================

/**
 * Determines if options should be loaded based on dependencies and current state
 */
export const shouldLoadOptions = (
  dependenciesSatisfied: boolean,
  dependenciesChanged: boolean,
  optionsLength: number
): boolean => {
  if (!dependenciesSatisfied) {
    return false;
  }

  return dependenciesChanged || optionsLength === 0;
};

/**
 * Resets options loading state
 */
export const resetOptionsLoadingState = (
  setOptions: (options: MenuInputOption[]) => void,
  setSelectedOptions?: (options: MenuInputOption[]) => void,
  setIsLoading?: (loading: boolean) => void,
  setPage?: (page: number) => void,
  setHasMoreOptions?: (hasMore: boolean) => void,
  prevOptionsRef?: MutableRefObject<string>
): void => {
  setOptions([]);
  setSelectedOptions?.([]);
  setIsLoading?.(true);
  setPage?.(0);
  setHasMoreOptions?.(true);

  if (prevOptionsRef) {
    // eslint-disable-next-line no-param-reassign
    prevOptionsRef.current = '';
  }
};

// Create loadMoreOptions callback for HTTP/SQL data providers
export const createLoadMoreOptions =
  (
    dataProvider: DataProvider,
    formValues: FormValues,
    fieldId: string,
    getDataProviderPreview: (
      payload: PreviewDataProviderPayload,
      options?: {
        onSuccess?: (data: ApiResponse) => void;
        onError?: (error: ApiError) => void;
      },
      dependenciesChangedOverride?: boolean,
      signal?: AbortSignal
    ) => void,
    setIsLoading: (loading: boolean) => void,
    setIsLoadingMore: (loading: boolean) => void,
    setOptions: (
      options:
        | MenuInputOption[]
        | ((prev: MenuInputOption[]) => MenuInputOption[])
    ) => void,
    setHasMoreOptions: (hasMore: boolean) => void,
    processedValue: MenuInputOption | MenuInputOption[] | null,
    paginationEnabled?: boolean,
    setSelectedOptions?: (options: MenuInputOption[]) => void,
    initialData?: InitialDataForDependencies,
    urlSlug?: string,
    searchText?: string,
    valuesToSearch?: unknown,
    /** Optional map of fieldName → fieldType for type-aware dependency formatting (e.g. date fields). */
    fieldTypeMap?: Map<string, string>
  ) =>
  (
    currentPage: number,
    hasMoreOptions: boolean,
    signal?: AbortSignal
  ): void => {
    if (
      dataProvider.type !== DATA_PROVIDER.HTTP &&
      dataProvider.type !== DATA_PROVIDER.SQL &&
      dataProvider.type !== DATA_PROVIDER.DATASTORE
    ) {
      return;
    }

    if (!hasMoreOptions) {
      return;
    }

    if (currentPage === 0) {
      setIsLoading(true);
    } else {
      setIsLoadingMore(true);
    }

    const dependencies = buildDependencies(
      dataProvider,
      formValues,
      initialData,
      fieldTypeMap
    );

    const payload: PreviewDataProviderPayload = {
      formItemId: fieldId,
      urlSlug,
      page: currentPage,
      pageSize: getDataProviderPreviewPageSize(dataProvider),
      dependencies,
      searchText: searchText ?? '',
      ...(shouldAttachValuesToSearch(valuesToSearch) ? { valuesToSearch } : {}),
    };

    getDataProviderPreview(
      payload,
      {
        onSuccess: (response: ApiResponse) => {
          setIsLoading(false);
          setIsLoadingMore(false);

          if (response.error || response.errorMessage) {
            return;
          }

          const rawInner = response.data.data as unknown;
          const baseRows = validateApiResponseData(
            extractDataProviderOptionRows(rawInner)
          );
          const defaultRows = validateApiResponseData(
            extractDataProviderDefaultSelection(rawInner)
          );
          const mergedOptions = mergeDefaultSelectionIntoOptions(
            baseRows,
            defaultRows,
            dataProvider
          );
          const effectiveProcessed = coalesceMultiSelectProcessedValue(
            processedValue,
            defaultRows
          );
          const { hasNext } = response.data;

          if (currentPage === 0) {
            if (paginationEnabled) {
              const matchedOptions = applyValueMatching(
                mergedOptions,
                effectiveProcessed
              );

              setOptions(matchedOptions);

              if (setSelectedOptions) {
                const checkedOnPage = matchedOptions.filter((option) =>
                  Boolean(option.isChecked)
                );
                const mergedSelected = mergePaginatedMultiSelectSelections(
                  matchedOptions,
                  checkedOnPage,
                  effectiveProcessed
                );

                setSelectedOptions(mergedSelected);
              }
            } else {
              const matchedOptions = applyValueMatching(
                mergedOptions,
                effectiveProcessed
              );

              setOptions(matchedOptions);

              if (setSelectedOptions) {
                setSelectedOptions(
                  validateSelectedOptions(
                    matchedOptions.filter((option) => Boolean(option.isChecked))
                  )
                );
              }
            }
          } else {
            setOptions((prevOptions) => {
              const validatedPrev = prevOptions.filter(
                (opt): opt is MenuInputOption =>
                  opt != null &&
                  typeof opt === 'object' &&
                  'value' in opt &&
                  'label' in opt &&
                  opt.value !== undefined &&
                  opt.value !== null &&
                  opt.label !== undefined &&
                  opt.label !== null
              );

              return [...validatedPrev, ...mergedOptions];
            });
          }

          setHasMoreOptions(hasNext);
        },
        onError: () => {
          setIsLoading(false);
          setIsLoadingMore(false);
        },
      },
      undefined,
      signal
    );
  };
